import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { PayoutReconSummary, Provider, reconcilePayouts } from '../../api/batch5Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Choose the provider (Swiggy or Zomato) and upload your ERP file and the provider file (both .xlsx, first sheet).',
  'Orders are matched by Order ID. Only orders marked Delivered on the provider side are kept, and the expected payout is recomputed from the ERP figures.',
  'Click Run Reconciliation. The full Excel report downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
  /** Backend route; the Reconciliation Tools hub points this at its own route. */
  endpoint?: string;
  backLabel?: string;
}

const num = (n: number) => n.toLocaleString('en-GB', { maximumFractionDigits: 2 });

function PayoutReconPanel({ mission, onBack, endpoint, backLabel }: PanelProps) {
  const [provider, setProvider] = useState<Provider>('swiggy');
  const [erp, setErp] = useState<File[]>([]);
  const [providerFile, setProviderFile] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<PayoutReconSummary>>();

  const label = provider === 'swiggy' ? 'Swiggy' : 'Zomato';

  const handleSubmit = async () => {
    if (erp.length === 0 || providerFile.length === 0) {
      fail('Upload both the ERP file and the provider file.');
      return;
    }
    await run(() => reconcilePayouts(erp[0], providerFile[0], provider, endpoint), 'Reconciliation failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide backLabel={backLabel}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Required file structure</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <strong>ERP file:</strong> one row per order with Order Id, Sales, Discount and{' '}
            {provider === 'swiggy' ? 'Packaging Charge [CART - SWIGGY]' : 'Restaurant Packaging Charges'}.
          </li>
          <li>
            <strong>{label} file:</strong> the provider's own export, one row per order. Column names are matched
            flexibly, so minor header variations are usually fine.
          </li>
        </ul>
      </div>

      <div className="mb-5 flex gap-2">
        {(['swiggy', 'zomato'] as Provider[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setProvider(p)}
            style={provider === p ? { backgroundColor: mission.color } : undefined}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              provider === p ? 'text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {p === 'swiggy' ? 'Swiggy' : 'Zomato'}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop label="ERP file" required accept={mission.accept} hint={mission.maxSizeLabel} accentColor={mission.color} files={erp} onFiles={setErp} />
        <FileDrop
          label={`${label} file`}
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={providerFile}
          onFiles={setProviderFile}
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Reconciling…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {result.filename} downloaded ({s.provider}).
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Matched orders', s.summary.matchedOrderIds],
              ['Delivered matched', s.summary.deliveredMatched],
              ['Payout exact', s.summary.payoutExact],
              ['Payout within ±1', s.summary.payoutWithin1],
              ['Average payout gap (MAE)', s.summary.mae],
              ['Provider fixed total', s.summary.providerFixedTotal],
              ['Orders with variance charges', s.summary.providerVariableRows],
            ].map(([name, value]) => (
              <div key={name} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                <div className="text-lg font-bold text-foreground">{num(Number(value))}</div>
                <div className="text-xs text-muted-foreground">{name}</div>
              </div>
            ))}
          </div>
          {s.variance_count === 0 ? (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
              No variance orders found: everything reconciled within tolerance.
            </div>
          ) : (
            <div className="rounded-lg border border-border">
              <p className="px-4 py-2 text-sm font-semibold text-foreground">
                Top variances ({s.variance_count} order(s) with |diff| &gt; 1; full list in the report)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted text-muted-foreground">
                    <tr>
                      {['Order ID', 'Predicted', 'Provider', 'Diff', 'Reason'].map((h) => (
                        <th key={h} className="px-3 py-1.5 font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.top_variances.map((v) => (
                      <tr key={v.order_id} className="border-t border-border">
                        <td className="px-3 py-1.5">{v.order_id}</td>
                        <td className="px-3 py-1.5">{v.predicted}</td>
                        <td className="px-3 py-1.5">{v.provider}</td>
                        <td className="px-3 py-1.5 font-medium text-red-700">{v.diff}</td>
                        <td className="px-3 py-1.5">{v.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload both files above to run the reconciliation.</InfoNotice>}
    </MissionShell>
  );
}

export default PayoutReconPanel;
